import { AOIKUMO_ACTIVE_READ_ONLY_FEATURES } from 'dashboard/aoikumoReadOnlyFeatures';
import { hasAoikumoAccess } from 'dashboard/helper/aoikumoAccess';
import {
  aoikumoGet,
  aoikumoPost,
  aoikumoPatch,
  aoikumoDelete,
  aoikumoPatchBranch,
  parseAoikumoError,
} from './aoikumoApi';

export const state = {
  // One item per connected Aoikumo key pair, oldest first:
  // { integration_id, name, subscription_key_last4, linked_branch_count, ... }
  // An empty list means "not connected".
  integrations: [],
  isFetched: false,
  fetchedAccountId: null,
};

export const getters = {
  getIntegrations(state) {
    return state.integrations;
  },
  getIntegrationById: state => integrationId =>
    state.integrations.find(item => item.integration_id === integrationId) ||
    null,
  integrationCount(state) {
    return state.integrations.length;
  },
  isConnected(state) {
    return state.integrations.length > 0;
  },
  // Names are only shown on tags once there is something to tell apart.
  hasMultipleIntegrations(state) {
    return state.integrations.length > 1;
  },
  isFetched(state) {
    return state.isFetched;
  },
  // A branch linked to an Aoikumo outlet is managed there (backend 409s edits).
  // `provider_links.aoikumo` is `{ integration_id, integration_name, external_id }`.
  isLinkedBranch: (state, getters) => branch =>
    getters.isConnected && !!branch?.provider_links?.aoikumo?.external_id,
  // Nothing is copied from Aoikumo until a branch is linked to an outlet, so
  // the pages only lock (and only warn) once at least one branch is linked,
  // in any of the integrations.
  linkedBranchCount: state =>
    state.integrations.reduce(
      (total, item) => total + (item.linked_branch_count || 0),
      0
    ),
  hasLinkedBranch: (state, getters) => getters.linkedBranchCount > 0,
  isReadOnlyRoute: (state, getters) => routeName =>
    getters.isConnected &&
    getters.hasLinkedBranch &&
    AOIKUMO_ACTIVE_READ_ONLY_FEATURES.includes(routeName),
  // Connected but nothing linked yet: pages stay editable and show a hint.
  isNeedsLinkRoute: (state, getters) => routeName =>
    getters.isConnected &&
    !getters.hasLinkedBranch &&
    AOIKUMO_ACTIVE_READ_ONLY_FEATURES.includes(routeName),
};

export const mutations = {
  setIntegrations(state, integrations) {
    state.integrations = Array.isArray(integrations) ? integrations : [];
  },
  setIsFetched(state, isFetched) {
    state.isFetched = isFetched;
  },
  setFetchedAccountId(state, accountId) {
    state.fetchedAccountId = accountId;
  },
  resetConnection(state) {
    state.integrations = [];
    state.isFetched = false;
    state.fetchedAccountId = null;
  },
};

export const actions = {
  async fetchIntegrations(
    { commit, rootGetters, state },
    { id, force = false } = {}
  ) {
    const accountId = id || rootGetters.getCurrentAccountId;
    if (!accountId) return;
    if (state.isFetched && state.fetchedAccountId === accountId && !force)
      return;

    const hasAccess = hasAoikumoAccess(
      rootGetters['accounts/isFeatureEnabledonAccount'],
      accountId
    );
    if (!hasAccess) {
      commit('resetConnection');
      commit('setIsFetched', true);
      commit('setFetchedAccountId', accountId);
      return;
    }

    try {
      const { data } = await aoikumoGet(accountId);
      commit('setIntegrations', data);
      commit('setIsFetched', true);
      commit('setFetchedAccountId', accountId);
    } catch (error) {
      // Leave isFetched false so the next call retries; pages stay editable.
      commit('setIntegrations', []);
      throw parseAoikumoError(error);
    }
  },

  // Add a new integration (one Aoikumo key pair). The backend checks the pair
  // with Aoikumo first. Resolves with the created integration.
  async addIntegration(
    { dispatch, rootGetters },
    { name, subscriptionKey, subscriptionSecret }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    let created;
    try {
      const { data } = await aoikumoPost(accountId, '', {
        name,
        subscription_key: subscriptionKey,
        subscription_secret: subscriptionSecret,
      });
      created = data;
    } catch (error) {
      throw parseAoikumoError(error);
    }
    await dispatch('fetchIntegrations', { id: accountId, force: true });
    return created;
  },

  // Rename and/or replace the key. Send `name`, or `subscriptionKey` together
  // with `subscriptionSecret`, or all three. A replaced key must belong to the
  // same Aoikumo tenant (the links are kept).
  async updateIntegration(
    { dispatch, rootGetters },
    { integrationId, name, subscriptionKey, subscriptionSecret }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    const body = {};
    if (name !== undefined) body.name = name;
    if (subscriptionKey && subscriptionSecret) {
      body.subscription_key = subscriptionKey;
      body.subscription_secret = subscriptionSecret;
    }
    let updated;
    try {
      const { data } = await aoikumoPatch(accountId, `/${integrationId}`, body);
      updated = data;
    } catch (error) {
      throw parseAoikumoError(error);
    }
    await dispatch('fetchIntegrations', { id: accountId, force: true });
    return updated;
  },

  // Destructive: unlinks every branch of the integration and deletes it.
  async removeIntegration({ dispatch, rootGetters }, { integrationId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await aoikumoDelete(accountId, `/${integrationId}`);
    } catch (error) {
      throw parseAoikumoError(error);
    }
    await dispatch('fetchIntegrations', { id: accountId, force: true });
  },

  // Link or move a branch (integrationId + outlet), or unlink it (outlet = null).
  // The backend checks the outlet with Aoikumo before saving, so this can take
  // a second or two. Errors keep the HTTP status and the backend's detail text.
  async setBranchLink(
    { dispatch, rootGetters },
    { branchId, integrationId, outlet }
  ) {
    const accountId = rootGetters.getCurrentAccountId;
    const link = outlet
      ? { integration_id: integrationId, external_id: String(outlet) }
      : null;
    try {
      await aoikumoPatchBranch(accountId, branchId, {
        provider_links: { aoikumo: link },
      });
    } catch (error) {
      throw parseAoikumoError(error);
    }
    // The linked-branch counts drive the read-only pages and the list.
    await dispatch('fetchIntegrations', { id: accountId, force: true });
  },

  // Sync one integration. `entityType` is optional: service | staff | appointment.
  async syncIntegration({ rootGetters }, { integrationId, entityType } = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    const suffix = entityType
      ? `?entity_type=${encodeURIComponent(entityType)}`
      : '';
    try {
      await aoikumoPost(accountId, `/${integrationId}/sync${suffix}`);
    } catch (error) {
      throw parseAoikumoError(error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
